import type { VarlessCopyId } from '@npc/copy'

import { t } from '../../copy'
import { useState } from 'react'

import { T } from '../../copy/text'
import type { ConversationMode } from './backend-contract'
import { RECENT_LIMIT, type RecentProjectsState } from './launch-state'
import { INIT_STAGES, type StageRow } from './provisioning'
import { copyFor, exitsFor, folderFlowAvailable, mayProceed, type FolderVerdict } from './folder'
import { canPickLocalFolder, type Surface } from './platform'

/**
 * 新会话启动器（设计稿 Z01 / Z02 / Z10）。
 *
 * # 它是主聊天区的一次切换，不是覆盖层
 *
 * SPEC-components §11 写死了：不盖旧聊天、不占 Sidecar、无遮罩。
 * 所以这个组件就是普通的页面内容，**没有 fixed、没有遮罩、没有 z-index**。
 *
 * # 信息层级是固定的，不是排版偏好
 *
 * 设计稿把顺序写成了合同（PRD §4）：
 *   1. 单 Agent / 团队二选一   ← 它决定后面界面上有没有团队入口
 *   2. 「直接开始」主操作，标注「不关联项目」
 *   3. 搜索
 *   4. 最近远程项目，最多 6 个
 *   5. 新建远程项目 / 连接远程仓库 / 导入已有项目
 *   6. 仅 macOS：选择本地开发文件夹 / 新建本地开发文件夹
 *
 * 第 1 步排在最前面是有理由的：它是这一屏唯一一个**之后改不回来**的选择
 * （单 Agent 能升成团队，团队降不回单 Agent）。把不可逆的那一步放在
 * 用户还没投入任何东西的时候问，代价最小。
 */

export interface LaunchProps {
  readonly surface: Surface
  readonly canStart?: boolean
  readonly canCreateProject?: boolean
  /** 选过文件夹之后的判定。`null` = 还没选。**Web 上永远是 null。** */
  readonly folderVerdict?: FolderVerdict | null
  /** 三阶段。`null`/缺省 = 没在建项目，整块不渲染。 */
  readonly initStages?: readonly StageRow[] | null
  /** 服务端说的 ready。**不是我们自己算的**——见 provisioning.ts。 */
  readonly initReady?: boolean
  /**
   * 选中一个本机文件夹之后往上报。**Web 上不会被调用**——
   * 那两个按钮本身就在 `canPickLocalFolder(surface)` 里面。
   */
  /**
   * 点了「选择文件夹」/「新建文件夹」。`create` 区分那两个按钮。
   *
   * **Web 上这两个按钮压根不渲染**（外面那层 `canPickLocalFolder`），
   * 所以这个回调在 Web 上永远不会被调到——不是「被拦住」，是不存在。
   */
  readonly onFolderAction?: (create: boolean, projectId: string | null) => Promise<void>
  /**
   * 「用这个名字新建项目」。
   *
   * 不给这个回调时按钮仍然渲染但点不动——**那是错的形状**，
   * 所以调用方必须给。它在这里是可选的只因为搜索区是个纯展示组件，
   * 而 `behavior.test.tsx` 里有一条盯着「点了真的会调」。
   */
  readonly onCreateNamed?: (name: string) => Promise<void>
  readonly recent: RecentProjectsState
  readonly keyword: string
  readonly onKeywordChange: (next: string) => void
  readonly onStart: (mode: ConversationMode, projectId: string | null) => void
  readonly onRetryProjects: () => void
  readonly starting: boolean
  /** 上一次开会话失败了。**失败不跳转**，留在这一页并说出来。 */
  readonly startFailed?: boolean
  /** 后端此刻能不能记住单 Agent / 团队这个选择。见 backend-contract.ts。 */
  readonly modePersistable: boolean
}

export function LaunchPage({
  surface,
  canStart = true,
  canCreateProject = true,
  folderVerdict,
  initStages,
  initReady = false,
  startFailed = false,
  onFolderAction,
  onCreateNamed,
  recent,
  keyword,
  onKeywordChange,
  onStart,
  onRetryProjects,
  starting,
  modePersistable,
}: LaunchProps) {
  // 用户在最近列表里点中的项目。本机文件夹登记要往某个项目下挂，
  // 而**没选项目时不登记**——挂到一个猜出来的项目上比不挂糟得多。
  const [pickedProject, setPickedProject] = useState<string | null>(null)
  /**
   * 用户选的工作方式。**这是一个显式状态，不是两颗按钮各自的副作用。**
   *
   * 上一版两颗按钮直接建会话，而最近项目那一行硬编码 `onStart('solo', …)`
   * ——于是「先选团队、再选项目」会悄悄变成 solo。冻结合同的顺序是
   * **先选 solo/team，再选项目或者不选**，所以工作方式必须先落成状态，
   * 项目那一步只补上 projectId。
   */
  const [mode, setMode] = useState<'solo' | 'team' | null>(null)
  // 「新建远程项目（只填名字）」那条路。null = 表单没展开。
  //
  // 展开式而不是跳一页：设计稿这一块的整个主张就是「一步进对话，
  // 没有第二个确认页」，为建项目再开一页会破掉同一句话。
  const [newProjectName, setNewProjectName] = useState<string | null>(null)
  return (
    <div
      data-testid="lulu-launch"
      data-surface={surface}
      className="mx-auto w-full max-w-[820px] px-5 py-6" /* npc-token-allow: 设计稿启动页最大宽 820px，整站 token 无此档 */
    >
      <header className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-npc-display text-npc-section text-npc-lulu-t1">
            <T id="lulu_workbench.launch.title" />
          </h1>
          <p className="mt-1 text-npc-caption text-npc-lulu-t3">
            <T id="lulu_workbench.launch.sub" />
          </p>
        </div>
        {/*
          「直接开始」是这一屏的页头主操作（设计稿 launchWeb 的
          `head.actions:[['直接开始','p']]`）。

          **它不依赖项目列表**——设计稿在 launchStates 里逐字写着这句：
          列表还在读、读失败、一个项目都没有，这颗都照常可点。
          所以它放在页头，不在任何一块卡片里：卡片是讲解，这颗是出口。

          没先选工作方式就按默认走单 Agent（设计稿「默认」那一行：
          简单改动默认单 Agent —— 协作成本不该大于开发收益）；
          先选了团队再按这颗，就尊重那个选择。
        */}
        <button
          type="button"
          data-testid="lulu-launch-direct-start"
          disabled={starting || !canStart}
          onClick={() => onStart(mode ?? 'solo', null)}
          className="h-[30px] shrink-0 rounded-npc-chip bg-npc-lulu-t1 px-3.5 text-npc-caption font-medium text-npc-lulu-bg-0 hover:opacity-90 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
        >
          <T id="lulu_workbench.launch.direct_k" />
        </button>
      </header>

      {/* ① 单 Agent 还是团队。这一屏唯一一个之后改不回来的选择。 */}
      <section
        data-testid="lulu-launch-mode"
        className="mb-3 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 p-3.5"
      >
        <h2 className="font-npc-display text-npc-section-sm text-npc-lulu-t1">
          <T id="lulu_workbench.launch.mode_title" />
        </h2>
        <p className="mt-1 text-npc-caption text-npc-lulu-t3">
          <T id="lulu_workbench.launch.mode_desc" />
        </p>
        <dl className="mt-2.5 grid gap-1.5">
          <Row kId="lulu_workbench.launch.mode_solo_k" vId="lulu_workbench.launch.mode_solo_v" />
          <Row kId="lulu_workbench.launch.mode_team_k" vId="lulu_workbench.launch.mode_team_v" />
          <Row
            kId="lulu_workbench.launch.mode_change_k"
            vId="lulu_workbench.launch.mode_change_v"
          />
          <Row
            kId="lulu_workbench.launch.mode_default_k"
            vId="lulu_workbench.launch.mode_default_v"
          />
        </dl>
        {/*
          后端此刻收不下这个选择（POST /conversations 只收 {title}）。
          在他按下按钮之前就说清楚，而不是收下之后假装存了。
          不拦他——会话是真能建的。
        */}
        {modePersistable ? null : (
          <p
            data-testid="lulu-launch-mode-gap"
            className="mt-2.5 text-npc-caption text-npc-lulu-warn"
          >
            <T id="lulu_workbench.launch.mode_not_persisted" />
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="lulu-launch-start-solo"
            data-selected={mode === 'solo' ? 'yes' : 'no'}
            disabled={starting || !canStart}
            onClick={() => {
              setMode('solo')
              onStart('solo', pickedProject)
            }}
            className="h-[30px] rounded-npc-chip bg-npc-lulu-t1 px-3 text-npc-caption font-medium text-npc-lulu-bg-0 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
          >
            <T id="lulu_workbench.launch.start_solo" />
          </button>
          <button
            type="button"
            data-testid="lulu-launch-start-team"
            data-selected={mode === 'team' ? 'yes' : 'no'}
            disabled={starting || !canStart}
            onClick={() => {
              setMode('team')
              onStart('team', pickedProject)
            }}
            className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
          >
            <T id="lulu_workbench.launch.start_team" />
          </button>
        </div>

        {/* 开失败了：**留在这一页**，不跳转。跳过去会得到一个不存在的
            会话，而用户会以为是它自己坏了。重试就是再点一次上面那两个。 */}
        {startFailed ? (
          <p data-testid="lulu-start-failed" className="mt-2 text-npc-caption text-npc-lulu-bad">
            <T id="lulu_workbench.launch.start_failed" />
          </p>
        ) : null}
        {starting ? (
          <p data-testid="lulu-starting" className="mt-2 text-npc-caption text-npc-lulu-t3">
            <T id="lulu_workbench.launch.starting" />
          </p>
        ) : null}
      </section>

      {/* ② 直接开始，或者先选一个项目。 */}
      <section
        data-testid="lulu-launch-path"
        /* 画板这一块是 tone:'g'（绿）——它不是装饰：两块并排时，
           绿的那一块在说「这里是出口」，灰的那一块在说「这里是说明」。 */
        className="mb-3 rounded-npc-chip border border-npc-lulu-ok/30 bg-npc-lulu-ok/[0.06] p-3.5"
      >
        <h2 className="font-npc-display text-npc-section-sm text-npc-lulu-t1">
          <T id="lulu_workbench.launch.path_title" />
        </h2>
        {/* Web 上这句话解释「为什么这里没有本地文件夹入口」。 */}
        {/* macOS 上不出现——那儿它不是缺失，那儿它就在下面。 */}
        {canPickLocalFolder(surface) ? null : (
          <p
            data-testid="lulu-launch-no-local-note"
            className="mt-1 text-npc-caption text-npc-lulu-t3"
          >
            <T id="lulu_workbench.launch.path_desc_web" />
          </p>
        )}
        <dl className="mt-2.5 grid gap-1.5">
          <Row kId="lulu_workbench.launch.direct_k" vId="lulu_workbench.launch.direct_v" />
          <Row
            kId="lulu_workbench.launch.title_shows_k"
            vId="lulu_workbench.launch.title_shows_v"
          />
          <Row kId="lulu_workbench.launch.picked_k" vId="lulu_workbench.launch.picked_v" />
          {/*
            Z01 这一块的第四行。上一版漏了它，而漏的原因值得记：
            我照着交接包的 SPEC-components §11 那份**文字规格**做的，
            那份只写了信息层级 1–6，没有逐行内容；真正的画板在**另一个 zip**
            里（LuluV3.dc.html 第 2098 行）。两份都在，我只读了一份。
          */}
          <Row kId="lulu_workbench.launch.how_else_k" vId="lulu_workbench.launch.how_else_v" />
        </dl>
        {/*
          「新建远程项目」是设计稿这一块的次操作
          （launchWeb 的 `actions:[…,['新建远程项目','s'],…]`）。

          文案 `launch.new_project` 早就写在文案库里了，**而前端一处都没用**——
          先写好的一句话，配一个从来没建出来的按钮。

          同一块里的「连接远程仓库」「导入已有项目」这一轮不做：
          它们要另外的后端，做一颗点不动的按钮比没有更糟。
          两条已登记在 ACCEPTANCE.md，不靠谁记得。

          只填名字，用的是已有的 onCreateNamed——与搜索无结果那条
          「用「X」新建项目」同一条路，不新开第二个建项目入口。
        */}
        {/*
          Z01 第 2099 行，这一块自己的 actions：
            [['直接开始','p'],['新建远程项目','s'],'连接远程仓库','导入已有项目']

          「直接开始」在画板里出现**两次**——页头一次、这一块一次。
          上一版只做了页头那颗，于是这一块讲完两条路却没有出口，
          读完得回头去页头找。两颗指向同一个动作不是重复，
          是「讲到哪儿就能在哪儿动手」。

          后两个不造按钮：它们的流程设计稿自己写着「未出图 —— 平台用受控
          服务身份，细节待后端定」。后端确实有（/project-imports、
          /projects/{id}/repositories），但各自是一套多步流程，
          一颗按钮接不住。已登记在 ACCEPTANCE.md。
        */}
        {/* 画板这一块的按钮是**同一排**，不是各占一行。上一版把「新建远程项目」
            放进了下一个 div，于是屏上成了两行——arron 对着设计图指出来的。 */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="lulu-launch-path-direct-start"
            disabled={starting || !canStart}
            onClick={() => onStart(mode ?? 'solo', null)}
            className="h-[30px] shrink-0 rounded-npc-chip bg-npc-lulu-t1 px-3.5 text-npc-caption font-medium text-npc-lulu-bg-0 hover:opacity-90 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
          >
            <T id="lulu_workbench.launch.direct_k" />
          </button>
          {onCreateNamed === undefined || newProjectName !== null ? null : (
            <button
              type="button"
              data-testid="lulu-launch-new-project"
              disabled={!canCreateProject}
              onClick={() => setNewProjectName('')}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.launch.new_project" />
            </button>
          )}
          {/* 「导入已有项目」有真落点：`/projects/import` 那一屏就是导入向导。
              设计包 857 行明写「导入已有项目的四个阶段画在项目模块交接包里，
              **本包只给启动页入口**」——这颗就是那个入口。

              「连接远程仓库」**这一版仍然不做**，但理由换了：上一版我写的
              「后端没有」是错的（/projects/{id}/repositories 在），真实原因是
              设计包 858 行自己写着「凭据登记流程未出图 —— 平台用受控服务身份，
              细节待后端定」，而 console 里也没有任何一屏承接它。
              一颗点开是死路的按钮比没有更糟。已登记在 ACCEPTANCE.md。 */}
          <a
            data-testid="lulu-launch-import-project"
            href="/projects/import"
            className="flex h-[30px] items-center rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
          >
            <T id="lulu_workbench.launch.import_project" />
          </a>
        </div>
        {onCreateNamed === undefined || newProjectName === null ? null : (
          <div className="mt-2">
            {
              <div className="flex flex-wrap items-center gap-2">
                <input
                  data-testid="lulu-launch-new-project-name"
                  value={newProjectName}
                  autoFocus
                  aria-label={t('lulu_workbench.launch.new_project')}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="h-[30px] min-w-[200px] flex-1 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t1 outline-none" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
                />
                {/* **名字还空着的时候不许渲染 search_create。**

                    2026-08-30 在生产上实测到：点开这个表单整屏变成
                    "Something went wrong!"，控制台是

                      CopyError: 变量 keyword 是空的，填出来会是一句缺字的话

                    表单是用 `setNewProjectName('')` 打开的，于是那一刻
                    keyword 就是空串。**模板层拒绝空变量是对的**——它挡的
                    正是「用「」新建项目」这种缺字的话；错的是这一侧在还没有
                    名字的时候就去填那个空。

                    判据与 disabled 用同一个 `trim() === ''`：两处各写一个，
                    会出现「按钮说『用「   」新建项目』而它按不动」。 */}
                {newProjectName.trim() === '' ? (
                  <button
                    type="button"
                    data-testid="lulu-launch-new-project-confirm"
                    disabled
                    className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
                  >
                    <T id="lulu_workbench.launch.new_project" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="lulu-launch-new-project-confirm"
                    disabled={!canCreateProject}
                    onClick={() => void onCreateNamed(newProjectName)}
                    className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
                  >
                    <T id="lulu_workbench.launch.search_create" vars={{ keyword: newProjectName }} />
                  </button>
                )}
              </div>
            }
          </div>
        )}
      </section>

      {/*
        ⑥ 仅 macOS 的两个本地入口。
        Web 与手机上这一整块**不渲染**——不是禁用，是不存在。
        一个置灰的按钮仍然在说「这个功能存在，只是你现在用不了」，
        而在 Web 上它不是用不了，是这条路根本不通。
      */}
      {canPickLocalFolder(surface) ? (
        <section
          data-testid="lulu-launch-local"
          className="mb-3 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 p-3.5"
        >
          <h2 className="font-npc-display text-npc-section-sm text-npc-lulu-t1">
            <T id="lulu_workbench.launch.mac_only_title" />
          </h2>
          <p className="mt-1 text-npc-caption text-npc-lulu-t3">
            <T id="lulu_workbench.launch.mac_only_desc" />
          </p>
          <p className="mt-2 text-npc-caption text-npc-lulu-ok">
            <T id="lulu_workbench.launch.folder_readonly" />
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="lulu-launch-pick-folder"
              onClick={() => void onFolderAction?.(false, pickedProject)}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.launch.pick_folder" />
            </button>
            <button
              type="button"
              data-testid="lulu-launch-new-folder"
              onClick={() => void onFolderAction?.(true, pickedProject)}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.launch.new_folder" />
            </button>
          </div>

          {/* 选完之后的判定结果（Z13 五种）。**只在 macOS 这一支里**——
              `folderFlowAvailable` 在 Web 上回 false，而这整段就在
              `canPickLocalFolder(surface)` 里面，两道都得过。 */}
          {folderVerdict && folderFlowAvailable(surface) ? (
            <div
              data-testid="lulu-folder-verdict"
              data-kind={folderVerdict.kind}
              data-may-proceed={mayProceed(folderVerdict) ? 'yes' : 'no'}
              className="mt-3 rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-2"
            >
              <p className="text-npc-caption text-npc-lulu-t2">
                {folderVerdict.kind === 'git_dirty' ? (
                  <T
                    id="lulu_workbench.folder.git_dirty"
                    vars={{ count: String(folderVerdict.count) }}
                  />
                ) : folderVerdict.kind === 'taken' ? (
                  <T id="lulu_workbench.folder.taken" vars={{ project: folderVerdict.project }} />
                ) : (
                  <T id={copyFor(folderVerdict) as 'lulu_workbench.folder.empty'} />
                )}
              </p>

              {folderVerdict.kind === 'taken' ? (
                <p className="mt-1 text-npc-meta text-npc-lulu-bad">
                  <T id="lulu_workbench.folder.taken_why" />
                </p>
              ) : null}
              {folderVerdict.kind === 'stale' ? (
                <>
                  <p className="mt-1 text-npc-meta text-npc-lulu-t3">
                    <T id="lulu_workbench.folder.stale_not_broken" />
                  </p>
                  <p className="text-npc-meta text-npc-lulu-t3">
                    <T id="lulu_workbench.folder.stale_fix" />
                  </p>
                </>
              ) : null}

              <div className="mt-2 flex gap-2">
                {exitsFor(folderVerdict)
                  .filter((e) => e !== 'none')
                  .map((exit) => (
                    <button
                      key={exit}
                      type="button"
                      data-testid="lulu-folder-exit"
                      data-exit={exit}
                      className="rounded-npc-chip border border-npc-lulu-line-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t2"
                    >
                      <T
                        id={
                          exit === 'open_settings'
                            ? 'lulu_workbench.folder.open_settings'
                            : 'lulu_workbench.folder.pick_another'
                        }
                      />
                    </button>
                  ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* 新建项目的三阶段（Z11）。**没有百分比**——三步完成两步不显示 66%。
          `initStages` 由调用方从真实 `GET /projects/:id/init-status` 给，
          拿不到就整块不渲染：一个编出来的阶段列表比不显示糟得多。 */}
      {initStages ? (
        <section
          data-testid="lulu-init-stages"
          data-ready={initReady ? 'yes' : 'no'}
          className="mt-4 rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-1 p-3"
        >
          <ul className="grid gap-1">
            {initStages.map((row) => (
              <li
                key={row.stage}
                data-testid="lulu-init-stage"
                data-stage={row.stage}
                data-state={row.state}
                className="text-npc-caption text-npc-lulu-t2"
              >
                <T id={INIT_STAGE_COPY[row.stage] as 'lulu_workbench.init.stage_project'} />
                {/* 这一步成没成，用字说出来。
                    上一版只写进 data-state——屏幕上三行只有名字，谁也看不出
                    哪一步还没回来，而「任一未确认不显示已准备好」这句承诺
                    要成立，用户先得看得见是哪一步没回来。 */}
                <span
                  data-testid="lulu-init-stage-state"
                  className="ml-2 text-npc-meta text-npc-lulu-t3"
                >
                  <T id={INIT_STATE_COPY[row.state] as 'lulu_workbench.init.state_done'} />
                </span>
                {row.whyNotYet ? (
                  <span className="ml-2 text-npc-meta text-npc-lulu-t4">
                    <T id={row.whyNotYet} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {/* 三步全部回来才切走。**这一页永远不显示「已准备好」。** */}
          {initReady ? null : (
            <p className="mt-2 text-npc-meta text-npc-lulu-t3">
              <T id="lulu_workbench.init.not_ready_yet" />
            </p>
          )}
          <p className="text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.init.same_key" />
          </p>
        </section>
      ) : null}

      {/* ③④ 搜索 + 最近的项目。 */}
      <section
        data-testid="lulu-launch-recent"
        className="rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 p-3.5"
      >
        <div className="flex items-baseline gap-2">
          <h2 className="font-npc-display text-npc-section-sm text-npc-lulu-t1">
            <T id="lulu_workbench.launch.recent_title" />
          </h2>
          <span className="text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.launch.recent_meta" />
          </span>
        </div>

        <input
          type="search"
          data-testid="lulu-launch-search"
          value={keyword}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder={t('lulu_workbench.launch.search_hint')}
          aria-label={t('lulu_workbench.launch.search_hint')}
          className="mt-2.5 h-[30px] w-full rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-2.5 text-npc-caption text-npc-lulu-t1 placeholder:text-npc-lulu-t4" /* npc-token-allow: 设计稿桌面输入高 30px，整站 token 无此档 */
        />

        <div className="mt-2.5">
          <RecentList
            state={recent}
            onPick={(projectId) => {
              // 记一下他选的是哪个项目：本机文件夹要登记到某个项目下，
              // 而这是启动器里唯一说出「哪个项目」的地方。
              setPickedProject(projectId)
              // **用他选过的工作方式，不硬编码 solo。**
              // 还没选过的话默认 solo——那是后端的缺省，与不传一致；
              // 但只要他点过「团队」，这里就必须是 team。
              onStart(mode ?? 'solo', projectId)
            }}
            onRetry={onRetryProjects}
            onCreateNamed={onCreateNamed}
            canCreateProject={canCreateProject}
            onClearKeyword={() => onKeywordChange('')}
            disabled={starting || !canStart}
          />
        </div>

        {/* 画板 2104 行的 foot。上一版一个字都没渲染，而它同时回答两件事：
            为什么只列 6 个，以及同步状态读不到时写什么。
            后半句正是这一屏「未核验」那个角标的依据——把依据藏起来，
            用户只会看到一个没来由的黄字。 */}
        <p data-testid="lulu-launch-recent-foot" className="mt-2 text-npc-meta text-npc-lulu-t4">
          <T id="lulu_workbench.launch.recent_foot" />
        </p>
      </section>
    </div>
  )
}

function Row({ kId, vId }: { kId: VarlessCopyId; vId: VarlessCopyId }) {
  return (
    <div className="flex gap-2.5">
      <dt className="w-[84px] shrink-0 text-npc-caption text-npc-lulu-t3">
        {' '}
        {/* npc-token-allow: 设计稿键列宽 84px，整站 token 无此档 */}
        <T id={kId} />
      </dt>
      <dd className="min-w-0 flex-1 text-npc-caption text-npc-lulu-t2">
        <T id={vId} />
      </dd>
    </div>
  )
}

function RecentList({
  state,
  onPick,
  onRetry,
  onClearKeyword,
  onCreateNamed,
  canCreateProject,
  disabled,
}: {
  state: RecentProjectsState
  onPick: (projectId: string) => void
  onRetry: () => void
  onClearKeyword: () => void
  onCreateNamed?: (name: string) => Promise<void>
  canCreateProject: boolean
  disabled: boolean
}) {
  // 每一档各画各的。**刻意不写成「有数据就画列表，没数据就画空态」**——
  // 那个二分法正是把「读不到」和「没有」合流的地方。
  switch (state.kind) {
    case 'loading':
      return (
        <p data-testid="lulu-launch-loading" className="text-npc-caption text-npc-lulu-t3">
          <T id="lulu_workbench.launch.loading_desc" />
        </p>
      )

    case 'failed':
      return (
        <div data-testid="lulu-launch-failed">
          <p className="text-npc-caption text-npc-lulu-warn">
            <T id="lulu_workbench.launch.read_failed" />
          </p>
          <button
            type="button"
            data-testid="lulu-launch-retry"
            onClick={onRetry}
            className="mt-2 h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
          >
            <T id="lulu_workbench.launch.retry" />
          </button>
        </div>
      )

    case 'empty':
      return (
        <div data-testid="lulu-launch-empty">
          <p className="text-npc-caption text-npc-lulu-t2">
            <T id="lulu_workbench.launch.really_none" />
          </p>
        </div>
      )

    case 'no_match':
      return (
        <div data-testid="lulu-launch-no-match">
          <p className="text-npc-caption text-npc-lulu-t2">
            <T id="lulu_workbench.launch.search_none" vars={{ keyword: state.keyword }} />
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="lulu-launch-clear-search"
              onClick={onClearKeyword}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.launch.search_clear" />
            </button>
            <button
              type="button"
              data-testid="lulu-launch-create-named"
              disabled={state.keyword.trim() === '' || !canCreateProject}
              onClick={() => void onCreateNamed?.(state.keyword)}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.launch.search_create" vars={{ keyword: state.keyword }} />
            </button>
          </div>
        </div>
      )

    case 'ready':
      return (
        <ul data-testid="lulu-launch-list" className="grid gap-1.5">
          {state.projects.slice(0, RECENT_LIMIT).map((project, index) => (
            <li key={project.id}>
              <button
                type="button"
                data-testid="lulu-launch-project"
                disabled={disabled}
                data-project={project.id}
                onClick={() => onPick(project.id)}
                className="flex w-full items-center gap-2 rounded-npc-chip border border-npc-lulu-line-1 bg-npc-lulu-bg-2 px-2.5 py-2 text-left disabled:opacity-50"
              >
                {/* 画板每行左侧的序号药丸。**纯下标，不是后端给的排名**——
                    它说的是「这一屏从上往下第几个」，而列表本身按最近活动排。 */}
                <span
                  data-testid="lulu-launch-project-index"
                  className="shrink-0 rounded-npc-chip-inline bg-npc-lulu-bg-3 px-1.5 text-npc-meta text-npc-lulu-t4"
                >
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-npc-caption text-npc-lulu-t1">
                  {project.name}
                </span>
                {/* 同步状态没核验过就写「未核验」。不用绿点也不填 0—— */}
                {/* 两者都是在替后端做一个它没给出的判断。 */}
                {project.synced === null ? (
                  <span
                    data-testid="lulu-launch-unverified"
                    className="shrink-0 text-npc-meta text-npc-lulu-warn"
                  >
                    <T id="lulu_workbench.launch.unverified" />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )
  }
}

/** 三阶段各自的标题。顺序由 provisioning.INIT_STAGES 定，这里只给词。 */
const INIT_STAGE_COPY: Readonly<Record<(typeof INIT_STAGES)[number], string>> = {
  project: 'lulu_workbench.init.stage_project',
  repo: 'lulu_workbench.init.stage_repo',
  conversation: 'lulu_workbench.init.stage_conversation',
}

/**
 * 每一档状态说的那句话。
 *
 * **`none` 与 `fail` 必须是两句不同的话**，因为用户要做的下一件事不同：
 * 「没有远程仓库」什么都不用做，「没成」要重试。上一版没有 none 这一档，
 * 于是从启动器建出来的每一个项目（它们全都没有仓库）都被说成失败。
 *
 * 这张表是 `Record<StageState, …>`，少一档编译就不过——新加一档状态
 * 而忘了给它一句话，那一格会渲染成 `undefined`，而那是一个空白，
 * 空白读起来像「加载坏了」。
 */
const INIT_STATE_COPY: Readonly<Record<StageRow['state'], string>> = {
  done: 'lulu_workbench.init.state_done',
  running: 'lulu_workbench.init.state_running',
  wait: 'lulu_workbench.init.state_wait',
  fail: 'lulu_workbench.init.state_fail',
  none: 'lulu_workbench.init.state_none',
}
